/**
 * Outfitting (plan §6 M2-05, spec §7.3 and §8): buy modules into the locker, sell them back at `sellFraction`,
 * and fit them into slots under the power budget. Pure and immutable; refusals are typed results.
 *
 * Salvaged modules (economy plan SY-5a, D8): an Intact Mk-carrying component installs as a module into
 * `ship.salvaged.locker` with no fitting fee, fits like a bought module and resells at component value × the best
 * station component multiplier, never at `sellFraction` of the module price. The commands are wired by SY-5c.
 *
 * Scrap (M8 plan MC-c7, RD8): `scrapModule` takes a locker module of the flown ship apart into materials in the hold,
 * never credits: `modules.json scrapFraction` of each ordinary recipe input (the carry never comes back), or a
 * salvaged module's break-down yield (SY-3).
 *
 * Band-agnostic modules (M10 P5a5a, spec §3.2): `moveModule` moves a locker module between two of your ships that are
 * together (the same station, or the carried S and its carrier) for free; a module has one price in every band.
 */
import { isGoodOrWare, recipeFor, systemFabricatesMk3, type FabSystem } from '../craft/recipes'
import { powerBudget as fitBudget, powerDraw as fitDraw, resolveModule } from '../data/m2'
import {
  MK_COMPONENT_TYPES,
  SLOT_IDS,
  isModuleId,
  moduleId as toModuleId,
  parseModuleId,
  type M2Data,
  type ModuleId,
  type ModuleKind,
  type ModuleMk,
  type SlotId,
  type StationDef,
  type StationId,
} from '../data/m2Schemas'
import { OWN_SHIP_ID, ownedShip, updateOwnedShip } from '../fleet-link/hangar'
import { CONDITION_BAND, conditionBand, partValue } from '../salvage/generate'
import { LOGGING, recordIncome } from '../story/logging'
import { BREAKDOWN_YIELDS, breakDownLines, type BreakDownYields } from './breakdown'
import { goodHoldT, holdCapacityT, hullFitData, hullOfDef, wareHoldT, type HullFitData } from './hullFit'
import { EPSILON, cargoUsedT, moduleValue, withGood, withWallet, withWare, withoutComponent } from './ops'
import { isGoodId } from './parts'
import { cargoFull, fail, formatTonnes, insufficientFunds, ok, type CmdFailure, type CmdResult } from './result'
import { SALVAGED_MODULE_KINDS } from './schema'
import type { CargoComponent, Career, GoodOrWare, MkComponentType, SalvagedModule, SalvagedState, ShipState } from './types'

/** Total power draw of a fit. */
export function powerDraw(fitted: Readonly<Record<SlotId, ModuleId>>, data: Pick<M2Data, 'modules'>): number {
  return fitDraw(data, fitted)
}

/** Power budget of a fit (the fitted power plant's budget). */
export function powerBudget(fitted: Readonly<Record<SlotId, ModuleId>>, data: Pick<M2Data, 'modules'>): number {
  return fitBudget(data, fitted)
}

/** Value (price) of every fitted module: the death rebuild fee builds on it. */
export function fittedValue(career: Career, data: Pick<M2Data, 'modules'>): number {
  let v = 0
  for (const slot of SLOT_IDS) v += moduleValue(data, career.ship.fitted[slot])
  return v
}

function unknownModule(id: string): CmdFailure {
  return fail('notSoldHere', `Unknown module: ${id}.`, { module: id })
}

function noOutfitting(station: StationDef): CmdFailure | null {
  if (station.services.includes('outfitting')) return null
  return fail('serviceUnavailable', `${station.name} has no outfitting service.`, { station: station.id })
}

/**
 * Buys `moduleId` at `station` into the locker at today's full price (the station must sell that module kind).
 * Ready-made Mk III sells only where Mk III can be fabricated (M8 plan decision 4, MC-c6): `system` is the docked
 * system (`SystemEconomyView`), and a Mk III is refused (`notSoldHere`, `detail.reason: 'mk3Geography'`) unless it has
 * a fabworks (the player's too) or a faction yard; null (no view) refuses it as well. Mk I and Mk II are unchanged.
 * A caller that passes no `system` (undefined) keeps the old any-station rule. A pattern module (`pdc.mk2.varro`,
 * D-511) is fabricate-only (MC-f2c): refused at every station, in every system (`notSoldHere`, `detail.reason:
 * 'fabricateOnly'`); it is made by a fab order once its rung is unlocked (`craft/fabOrder.ts startFab`).
 */
export function buyModule(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  station: StationDef,
  moduleId: ModuleId,
  system?: FabSystem | null,
): CmdResult<Career> {
  if (!isModuleId(moduleId)) return unknownModule(String(moduleId))
  const refused = noOutfitting(station)
  if (refused) return refused
  const m = resolveModule(data, moduleId)
  if (parseModuleId(moduleId).pattern !== undefined) {
    return fail('notSoldHere', `${m.name} is fabricate-only: it is never sold for credits.`, {
      station: station.id,
      module: moduleId,
      reason: 'fabricateOnly',
    })
  }
  if (!station.outfitting.includes(m.kind)) {
    return fail('notSoldHere', `${station.name} does not sell ${m.name}.`, { station: station.id, module: moduleId })
  }
  if (system !== undefined && parseModuleId(moduleId).mk === 3 && !systemFabricatesMk3(system)) {
    return fail('notSoldHere', `${m.name} is sold only in systems with a fabworks or a shipyard.`, {
      station: station.id,
      module: moduleId,
      reason: 'mk3Geography',
    })
  }
  if (career.wallet + EPSILON < m.price) return insufficientFunds(m.price, career.wallet)
  const next = withWallet(career, -m.price)
  return ok({ ...next, ship: { ...next.ship, locker: [...next.ship.locker, moduleId] } })
}

/** Sells one `moduleId` from the locker for `price × sellFraction` (fitted modules must be unfitted first). */
export function sellModule(career: Career, data: Pick<M2Data, 'modules'>, station: StationDef, moduleId: ModuleId): CmdResult<Career> {
  if (!isModuleId(moduleId)) return unknownModule(String(moduleId))
  const refused = noOutfitting(station)
  if (refused) return refused
  const at = career.ship.locker.indexOf(moduleId)
  if (at < 0) return fail('notInLocker', `${resolveModule(data, moduleId).name} is not in your locker.`, { module: moduleId })
  const locker = career.ship.locker.filter((_, i) => i !== at)
  const next = withWallet(career, moduleValue(data, moduleId) * data.modules.sellFraction)
  return ok({ ...next, ship: { ...next.ship, locker } })
}

/**
 * Fits `moduleId` from the locker into `slot`; the module it replaces goes to the locker. Refuses a slot that
 * does not take the kind (`slotMismatch`), a module not in the locker (`notInLocker`), a fit whose draw exceeds
 * its budget (`powerBudget`, naming both) and a smaller cargo rack the hold would not fit into (`cargoFull`).
 */
export function fitModule(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  station: StationDef,
  slot: SlotId,
  moduleId: ModuleId,
): CmdResult<Career> {
  if (!isModuleId(moduleId)) return unknownModule(String(moduleId))
  if (!SLOT_IDS.includes(slot)) return fail('slotMismatch', `Unknown slot: ${String(slot)}.`, { slot: String(slot) })
  const refused = noOutfitting(station)
  if (refused) return refused
  const m = resolveModule(data, moduleId)
  if (!data.modules.slots[slot].includes(m.kind)) {
    return fail('slotMismatch', `${m.name} does not fit the ${slot} slot.`, { slot, module: moduleId })
  }
  const at = career.ship.locker.indexOf(moduleId)
  if (at < 0) return fail('notInLocker', `${m.name} is not in your locker.`, { module: moduleId })
  const fitted = { ...career.ship.fitted, [slot]: moduleId }
  const draw = powerDraw(fitted, data)
  const budget = powerBudget(fitted, data)
  if (draw > budget) {
    return fail('powerBudget', `Not enough power: the fit would draw ${draw} of a ${budget} budget.`, { draw, budget })
  }
  let capacityT = career.cargo.capacityT
  if (slot === 'cargo') {
    // M10 P5a4a/P5a6a: the hull-rated hold (`hullFit.ts holdCapacityT`; the rack's own `capacityT` with no `fit`).
    capacityT = holdCapacityT(career.ship.defId, fitted, data)
    const used = cargoUsedT(career)
    if (used > capacityT + EPSILON) {
      return fail(
        'cargoFull',
        `The hold carries ${formatTonnes(used)} t but ${m.name} holds only ${formatTonnes(capacityT)} t.`,
        { needed: used, capacity: capacityT },
      )
    }
  }
  const locker = career.ship.locker.filter((_, i) => i !== at)
  return ok({
    ...career,
    cargo: capacityT === career.cargo.capacityT ? career.cargo : { ...career.cargo, capacityT },
    ship: refit(career.ship, slot, fitted, locker, salvagedOf(career.ship).locker, null),
  })
}

// ---------------------------------------------------------------------------------------------
// Salvaged modules (economy plan SY-5a; D8, D2-FC #7)
// ---------------------------------------------------------------------------------------------

const EMPTY_SALVAGED: SalvagedState = { locker: [], fitted: {} }

/** The ship's salvaged-module provenance (empty when the key is absent: a pre-SY-5a save). */
export function salvagedOf(ship: Pick<ShipState, 'salvaged'>): SalvagedState {
  return ship.salvaged ?? EMPTY_SALVAGED
}

/**
 * The ship after `fitted` (which already holds the incoming module in `slot`): the module it replaces goes to
 * `salvaged.locker` when it was a salvaged one (provenance kept), else to the plain `locker`. `incoming` is the
 * salvaged entry now in `slot`, or null for a bought module. A ship that never had salvaged modules gets no key.
 */
function refit(
  ship: ShipState,
  slot: SlotId,
  fitted: Record<SlotId, ModuleId>,
  plainLocker: readonly ModuleId[],
  salvagedLocker: readonly SalvagedModule[],
  incoming: SalvagedModule | null,
): ShipState {
  const was = salvagedOf(ship)
  const displaced = was.fitted[slot]
  const locker = displaced === undefined ? [...plainLocker, ship.fitted[slot]] : [...plainLocker]
  const sLocker = displaced === undefined ? [...salvagedLocker] : [...salvagedLocker, displaced]
  if (ship.salvaged === undefined && incoming === null && sLocker.length === 0) return { ...ship, fitted, locker }
  const sFitted: Partial<Record<SlotId, SalvagedModule>> = {}
  for (const s of SLOT_IDS) {
    const m = s === slot ? incoming : was.fitted[s]
    if (m !== undefined && m !== null) sFitted[s] = m
  }
  return { ...ship, fitted, locker, salvaged: { locker: sLocker, fitted: sFitted } }
}

function isMkComponentType(type: CargoComponent['type']): type is MkComponentType {
  return (MK_COMPONENT_TYPES as readonly string[]).includes(type)
}

/** A component's Mk as a module Mk: Mk I when absent (a pre-SY-4b save) or out of range. */
function componentMk(c: CargoComponent): ModuleMk {
  return c.mk === 2 ? 2 : c.mk === 3 ? 3 : 1
}

/**
 * The best component multiplier over the known stations (`stations.json prices.components`; the Scrapyard's ×1.25
 * today). 0 when no station buys components.
 */
export function bestComponentMultiplier(data: Pick<M2Data, 'stations'>): number {
  let best = 0
  for (const st of data.stations.stations) {
    const m = st.prices.components
    if (m !== null && m > best) best = m
  }
  return best
}

/**
 * What a salvaged module resells for (D8): its component's value at its condition (`partValue`) × the best station
 * component multiplier. Never `sellFraction` of the module price, so "salvage → install → sell" never beats selling
 * the component at the best station (D2-FC #7).
 */
export function salvagedSaleValue(
  data: Pick<M2Data, 'wares' | 'stations'>,
  m: Pick<SalvagedModule, 'type' | 'condition'>,
): number {
  return partValue(m.type, m.condition, data) * bestComponentMultiplier(data)
}

/**
 * Installs the hold component `componentId` as a salvaged module into `ship.salvaged.locker` at an outfitting
 * station (SY-5a; no fitting fee). Only an Intact (`salvage.json bands.intact`) Mk-carrying component installs:
 * reactor → `power.mkN`, shieldGen → `shield.mkN`, thruster → `thrusters.mkN`, weapon → `pdc.mkN` or `laser.mkN`
 * (`weaponKind`, default pdc; ignored for the other types). N is the component's Mk (Mk I when absent). Contract,
 * sealed and story components are refused (`contractRequirement`); the other refusals are `notInCargo`,
 * `serviceUnavailable` and `slotMismatch` (`detail.reason`: `notInstallable`, `condition` or `weaponKind`).
 */
export function installComponent(
  career: Career,
  data: Pick<M2Data, 'salvage' | 'wares'>,
  station: StationDef,
  componentId: string,
  weaponKind: ModuleKind = 'pdc',
): CmdResult<Career> {
  const refused = noOutfitting(station)
  if (refused) return refused
  const c = career.cargo.components.find((x) => x.id === componentId)
  if (c === undefined) return fail('notInCargo', 'That component is not in the hold.', { id: componentId })
  const name = data.wares.components.find((d) => d.type === c.type)?.name ?? c.type
  if (c.contractId !== undefined || c.sealed === true || c.story === true) {
    return fail('contractRequirement', `The ${name} belongs to a contract and can't be installed.`, { id: c.id })
  }
  if (!isMkComponentType(c.type)) {
    return fail('slotMismatch', `A ${name} can't be installed as a module.`, { id: c.id, reason: 'notInstallable' })
  }
  if (conditionBand(c.condition, data.salvage.bands) !== CONDITION_BAND.intact) {
    return fail('slotMismatch', `Only an Intact ${name} can be installed.`, { id: c.id, reason: 'condition', condition: c.condition })
  }
  const kinds = SALVAGED_MODULE_KINDS[c.type]
  const kind = kinds.length === 1 ? kinds[0] : weaponKind
  if (!kinds.includes(kind)) {
    return fail('slotMismatch', `A ${name} can't be installed as ${kind}.`, { id: c.id, reason: 'weaponKind', kind })
  }
  const mk = componentMk(c)
  // Always the base module, never the maker's pattern (MC-f2c, review 1 m15): salvage is not a free unlock. The
  // component's maker and Rush tag ride along (MC-0a).
  const entry: SalvagedModule = { moduleId: toModuleId(kind, mk), sourceId: c.id, type: c.type, condition: c.condition, mk }
  if (c.provenance !== undefined) entry.provenance = c.provenance
  if (c.maker !== undefined) entry.maker = c.maker
  const next = withoutComponent(career, c.id)
  const st = salvagedOf(next.ship)
  return ok({ ...next, ship: { ...next.ship, salvaged: { locker: [...st.locker, entry], fitted: st.fitted } } })
}

/**
 * Fits the salvaged module `sourceId` from `salvaged.locker` into `slot`, refusing as `fitModule` does
 * (`slotMismatch`, `notInLocker`, `powerBudget`, `serviceUnavailable`). The module it replaces goes back to its
 * locker: a salvaged one to `salvaged.locker` (provenance kept), a bought one to `locker`.
 */
export function fitSalvaged(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  station: StationDef,
  slot: SlotId,
  sourceId: string,
): CmdResult<Career> {
  if (!SLOT_IDS.includes(slot)) return fail('slotMismatch', `Unknown slot: ${String(slot)}.`, { slot: String(slot) })
  const refused = noOutfitting(station)
  if (refused) return refused
  const st = salvagedOf(career.ship)
  const at = st.locker.findIndex((m) => m.sourceId === sourceId)
  if (at < 0) return fail('notInLocker', 'That salvaged module is not in your locker.', { id: sourceId })
  const entry = st.locker[at]
  const m = resolveModule(data, entry.moduleId)
  if (!data.modules.slots[slot].includes(parseModuleId(entry.moduleId).kind)) {
    return fail('slotMismatch', `${m.name} does not fit the ${slot} slot.`, { slot, module: entry.moduleId })
  }
  const fitted = { ...career.ship.fitted, [slot]: entry.moduleId }
  const draw = powerDraw(fitted, data)
  const budget = powerBudget(fitted, data)
  if (draw > budget) {
    return fail('powerBudget', `Not enough power: the fit would draw ${draw} of a ${budget} budget.`, { draw, budget })
  }
  const sLocker = st.locker.filter((_, i) => i !== at)
  return ok({ ...career, ship: refit(career.ship, slot, fitted, career.ship.locker, sLocker, entry) })
}

/**
 * Sells the salvaged module `sourceId` from `salvaged.locker` at an outfitting station for `salvagedSaleValue`
 * (component value × the best station component multiplier; D8, no fee). A fitted one must be replaced first
 * (`notInLocker`). The credits count as earned and as salvage income (`LOGGING.componentSaleSource`).
 */
export function sellSalvaged(
  career: Career,
  data: Pick<M2Data, 'wares' | 'stations'>,
  station: StationDef,
  sourceId: string,
): CmdResult<Career> {
  const refused = noOutfitting(station)
  if (refused) return refused
  const st = salvagedOf(career.ship)
  const entry = st.locker.find((m) => m.sourceId === sourceId)
  if (entry === undefined) {
    const isFitted = Object.values(st.fitted).some((m) => m?.sourceId === sourceId)
    const msg = isFitted ? 'That salvaged module is fitted: replace it before selling.' : 'That salvaged module is not in your locker.'
    return fail('notInLocker', msg, { id: sourceId })
  }
  const credits = salvagedSaleValue(data, entry)
  if (credits <= 0) return fail('notBoughtHere', 'No station buys salvaged components.', { id: sourceId })
  const paid = withWallet(career, credits)
  const ship: ShipState = { ...paid.ship, salvaged: { locker: st.locker.filter((m) => m !== entry), fitted: st.fitted } }
  const sold: Career = { ...paid, ship, stats: { ...paid.stats, earned: paid.stats.earned + credits } }
  return ok(recordIncome(sold, LOGGING.componentSaleSource, credits))
}

// ---------------------------------------------------------------------------------------------
// Scrap (M8 plan MC-c7; RD8: materials, never credits)
// ---------------------------------------------------------------------------------------------

declare module '../craft/hook' {
  interface CraftCommandMap {
    /**
     * MC-c7: scrap the flown ship's locker module `id` at the docked station `stationId`: a salvaged module by its
     * `sourceId`, else a bought or fabricated module by its module id.
     */
    'module.scrap': { kind: 'module.scrap'; stationId: StationId; id: string }
  }
}

/** One item scrapping returns: tonnes of a ware, or units of a good. */
export interface ScrapLine {
  id: GoodOrWare
  qty: number
}

export const SCRAP_TEXTS = {
  closed: 'Scrapping modules is not open yet.',
  notInLocker: 'That module is not in your locker.',
  fitted: 'That module is fitted: replace it before scrapping.',
  nothing: 'There is nothing to recover from it.',
} as const

/**
 * What scrapping the bought or fabricated module `moduleId` returns: `modules.json scrapFraction` × each ordinary
 * input of its recipe (`recipeFor`, so a pattern module's signature input counts), in recipe order. The carry never
 * comes back, and the credit share is gone. Empty for a module without a recipe (Mk I) or without a `scrapFraction`.
 */
export function moduleScrapLines(data: Pick<M2Data, 'modules'>, moduleId: ModuleId): ScrapLine[] {
  const fraction = data.modules.scrapFraction ?? 0
  const recipe = fraction > 0 ? recipeFor(data, moduleId) : null
  const out: ScrapLine[] = []
  if (recipe === null) return out
  for (const [id, t] of Object.entries(recipe.inputs)) {
    const qty = (t ?? 0) * fraction
    if (isGoodOrWare(id) && qty > EPSILON) out.push({ id, qty })
  }
  return out
}

/**
 * What scrapping the flown ship's locker entry `id` returns (the Outfitting row's preview): a salvaged module (by
 * `sourceId`, read first) yields exactly its break-down goods (SY-3, `breakDownLines`); a bought or fabricated module
 * `moduleScrapLines`. Null when `id` is in neither locker.
 */
export function scrapLines(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  id: string,
  yields: BreakDownYields = BREAKDOWN_YIELDS,
): ScrapLine[] | null {
  const entry = salvagedOf(career.ship).locker.find((m) => m.sourceId === id)
  if (entry !== undefined) return breakDownLines(entry, yields).map((l) => ({ id: l.good, qty: l.qty }))
  if (!isModuleId(id) || !career.ship.locker.includes(id)) return null
  return moduleScrapLines(data, id)
}

function scrapLinesT(lines: readonly ScrapLine[], defId: string): number {
  let t = 0
  // M10 P5a6b H3: class-weighted on the flown hull (qty × mass and raw tonnes on every shipped hull).
  for (const l of lines) t += isGoodId(l.id) ? goodHoldT(defId, l.id, l.qty) : wareHoldT(defId, l.id, l.qty)
  return t
}

/**
 * Scraps the flown ship's locker entry `id` at an outfitting station into materials in the hold (`scrapLines`): no
 * credits move (RD8). The salvaged locker is read first (by `sourceId`), then the plain locker (by module id). Refuses
 * (Career untouched) while `goods.tradeEnabled` is off (it puts goods in the hold, like break-down), without
 * outfitting, for a module not in the locker or fitted (`notInLocker`), a module with nothing to recover (Mk I;
 * `notBoughtHere`) and materials that don't fit the hold (`cargoFull`; nothing is lost). Fleet ships in the hangar are
 * never touched.
 */
export function scrapModule(
  career: Career,
  data: Pick<M2Data, 'modules' | 'career'>,
  station: StationDef,
  id: string,
  yields: BreakDownYields = BREAKDOWN_YIELDS,
): CmdResult<Career> {
  if (!data.career.goods.tradeEnabled) return fail('serviceUnavailable', SCRAP_TEXTS.closed)
  const refused = noOutfitting(station)
  if (refused) return refused
  const st = salvagedOf(career.ship)
  const lines = scrapLines(career, data, id, yields)
  if (lines === null) {
    const fitted = Object.values(st.fitted).some((m) => m?.sourceId === id) || SLOT_IDS.some((s) => career.ship.fitted[s] === id)
    return fail('notInLocker', fitted ? SCRAP_TEXTS.fitted : SCRAP_TEXTS.notInLocker, { id })
  }
  if (lines.length === 0) return fail('notBoughtHere', SCRAP_TEXTS.nothing, { id })
  const needT = scrapLinesT(lines, career.ship.defId)
  const free = Math.max(0, career.cargo.capacityT - cargoUsedT(career))
  if (needT > free + EPSILON) return cargoFull(needT, free)
  const entry = st.locker.find((m) => m.sourceId === id)
  let ship: ShipState
  if (entry !== undefined) {
    ship = { ...career.ship, salvaged: { locker: st.locker.filter((m) => m !== entry), fitted: st.fitted } }
  } else {
    const at = isModuleId(id) ? career.ship.locker.indexOf(id) : -1
    ship = { ...career.ship, locker: career.ship.locker.filter((_, i) => i !== at) }
  }
  let next: Career = { ...career, ship }
  for (const l of lines) next = isGoodId(l.id) ? withGood(next, l.id, l.qty) : withWare(next, l.id, l.qty)
  return ok(next)
}


// ---------------------------------------------------------------------------------------------
// Band-agnostic modules (M10 P5a5a; hull tiers spec §3.2: one price, free moves between your ships)
// ---------------------------------------------------------------------------------------------

/**
 * Where the player's ships are for `moveModule`: the strategic station a ship (by fleet id, `OWN_SHIP_ID` = the lease
 * ship) is docked or moored at, or null (in space, or unknown). The caller reads it from the fleet view and the
 * docked station; the Career alone doesn't know where a fleet ship is.
 */
export interface ShipPlaces {
  stationOf(fleetId: string): string | null
}

/** The flown ship's fleet id (`career.flownShip`; absent = the lease ship, `OWN_SHIP_ID`). */
function flownIdOf(career: Pick<Career, 'flownShip'>): string {
  return career.flownShip ?? OWN_SHIP_ID
}

/** The locker and def of ship `fleetId`: `career.ship` while flown, else its hangar entry; null when it is not the player's. */
function lockerShip(career: Career, fleetId: string): { defId: string; locker: readonly ModuleId[] } | null {
  if (fleetId === flownIdOf(career)) return career.ship
  return ownedShip(career, fleetId)
}

/** True when the module kind `kind` has a slot on `defId`: a slot that takes the kind and, with a hull `fit`, is in `fit.slots`. */
export function hasSlotFor(defId: string, kind: ModuleKind, data: Pick<M2Data, 'modules'>, hulls: HullFitData = hullFitData()): boolean {
  const hull = hullOfDef(defId)
  const slots = hull === null ? undefined : hulls.hulls.get(hull)?.fit?.slots
  for (const s of SLOT_IDS) {
    if (!data.modules.slots[s].includes(kind)) continue
    if (slots === undefined || (slots[s] ?? 0) > 0) return true
  }
  return false
}

/**
 * Moves locker module `moduleId` from ship `fromFleetId`'s locker to ship `toFleetId`'s (M10 P5a5a). Free: the wallet
 * is untouched (modules are band-agnostic, one price; spec §3.2). Both ships must be the player's (the flown ship's
 * locker is `career.ship.locker`, the others' their hangar entry's) and together: the carried S and its carrier
 * (`career.carriedBy`), or both docked or moored at the same station (`places`). The module's kind needs a slot on
 * the target (`hasSlotFor`: with a hull `fit`, a `fit.slots` entry; without one, every slot). Salvaged modules stay
 * with their ship (their provenance is per ship, M6I-5). Refusals: `invalidAmount` (the same ship), `notInLocker`
 * (no such ship or module), `serviceUnavailable` (`detail.reason: 'notTogether'`) and `slotMismatch`.
 */
export function moveModule(
  career: Career,
  data: Pick<M2Data, 'modules'>,
  fromFleetId: string,
  toFleetId: string,
  moduleId: ModuleId,
  places: ShipPlaces,
  hulls: HullFitData = hullFitData(),
): CmdResult<Career> {
  if (!isModuleId(moduleId)) return unknownModule(String(moduleId))
  if (fromFleetId === toFleetId) return fail('invalidAmount', 'The module is already aboard that ship.', { ship: fromFleetId })
  const from = lockerShip(career, fromFleetId)
  if (from === null) return fail('notInLocker', `Ship ${fromFleetId} is not yours.`, { ship: fromFleetId })
  const to = lockerShip(career, toFleetId)
  if (to === null) return fail('notInLocker', `Ship ${toFleetId} is not yours.`, { ship: toFleetId })
  const at = from.locker.indexOf(moduleId)
  if (at < 0) return fail('notInLocker', `${moduleId} is not in that ship's locker.`, { module: moduleId, ship: fromFleetId })
  const flown = flownIdOf(career)
  const carrierPair =
    career.carriedBy !== undefined &&
    ((fromFleetId === flown && toFleetId === career.carriedBy) || (toFleetId === flown && fromFleetId === career.carriedBy))
  if (!carrierPair) {
    const a = places.stationOf(fromFleetId)
    const b = places.stationOf(toFleetId)
    if (a === null || b === null || a !== b) {
      return fail('serviceUnavailable', 'Both ships must be docked or moored at the same station.', { reason: 'notTogether', from: a ?? '', to: b ?? '' })
    }
  }
  const { kind } = parseModuleId(moduleId)
  if (!hasSlotFor(to.defId, kind, data, hulls)) {
    return fail('slotMismatch', `${moduleId} has no slot on that hull.`, { module: moduleId, ship: toFleetId })
  }
  const take = (locker: readonly ModuleId[]): ModuleId[] => locker.filter((_, i) => i !== at)
  const give = (locker: readonly ModuleId[]): ModuleId[] => [...locker, moduleId]
  let next: Career = career
  for (const [id, change] of [
    [fromFleetId, take],
    [toFleetId, give],
  ] as const) {
    next = id === flown ? { ...next, ship: { ...next.ship, locker: change(next.ship.locker) } } : updateOwnedShip(next, id, (e) => ({ ...e, locker: change(e.locker) }))
  }
  return ok(next)
}
